import React__default from 'react';
import RcInputNumber from 'rc-input-number';
import { Body } from '../Typography/index.es.js';
import { UpOutlined, DownOutlined } from '@teko/ds-icon';
import { Shape } from '../Shape/index.es.js';
import { MAPPING_SIZE } from '../../constants/index.es.js';
import { SizeContext } from '../../contexts/SizeContext.es.js';
import styled from '@emotion/styled';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const SAVED_DECIMAL_SEPARATOR = ".";
const InputNumber = React__default.forwardRef((_a, ref) => {
  var _b = _a, {
    size: customizeSize,
    addonBefore,
    addonAfter,
    bordered = true,
    disabled = false,
    readOnly = false
  } = _b, props = __objRest(_b, [
    "size",
    "addonBefore",
    "addonAfter",
    "bordered",
    "disabled",
    "readOnly"
  ]);
  var _a2, _b2;
  const size = React__default.useContext(SizeContext);
  const mergeSize = customizeSize || size || "m";
  const { decimalSeparator } = props;
  const formatter = (value) => {
    if (!value)
      return "";
    if (typeof value === "string") {
      value = parseFloat(value);
    }
    const locale = decimalSeparator === "." ? "en-CA" : "de-DE";
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 10
    }).format(value);
  };
  const parser = (value) => {
    if (!value) {
      return "";
    }
    const thousandSeparator = decimalSeparator === "." ? "," : ".";
    return value.replace(thousandSeparator, "").replace(decimalSeparator || ".", SAVED_DECIMAL_SEPARATOR);
  };
  const prefix = addonBefore ? /* @__PURE__ */ React__default.createElement(StyledSpace, {
    height: MAPPING_SIZE[mergeSize]
  }, typeof addonBefore === "string" ? /* @__PURE__ */ React__default.createElement(Body, {
    color: "textSecondary",
    level: 1,
    weight: "regular"
  }, addonBefore) : React__default.cloneElement(addonBefore, {
    size: ((_a2 = addonBefore == null ? void 0 : addonBefore.props) == null ? void 0 : _a2.size) || 20
  })) : null;
  const suffix = addonAfter ? /* @__PURE__ */ React__default.createElement(StyledSpace, {
    height: MAPPING_SIZE[mergeSize],
    marginLeft: "s16"
  }, typeof addonAfter === "string" ? /* @__PURE__ */ React__default.createElement(Body, {
    color: "textSecondary",
    level: 1,
    weight: "regular"
  }, addonAfter) : React__default.cloneElement(addonAfter, {
    size: ((_b2 = addonAfter == null ? void 0 : addonAfter.props) == null ? void 0 : _b2.size) || 20
  })) : null;
  return /* @__PURE__ */ React__default.createElement(Wrapper, {
    height: MAPPING_SIZE[mergeSize],
    disabled,
    readOnly,
    bordered,
    paddingRight: !!suffix ? "s16" : "s0",
    className: "wrap-input-number"
  }, prefix, /* @__PURE__ */ React__default.createElement(RcInputNumber, __spreadValues({
    ref,
    upHandler: /* @__PURE__ */ React__default.createElement(UpOutlined, {
      size: 12
    }),
    downHandler: /* @__PURE__ */ React__default.createElement(DownOutlined, {
      size: 12
    }),
    disabled,
    readOnly,
    formatter,
    parser
  }, props)), suffix);
});
InputNumber.displayName = "InputNumber";
const Wrapper = styled(Shape)(({ theme, disabled, readOnly, bordered }) => `
  ${disabled ? `background: ${theme == null ? void 0 : theme.colors.background}; 
         cursor: not-allowed;` : "background: white;"}
  ${bordered ? `border: 1px solid ${theme == null ? void 0 : theme.colors.border};
         border-radius: 4px;` : ""}
  &:focus-within{
    border-color: ${theme == null ? void 0 : theme.colors.link500};
  }
  display: flex;
  padding-left: 0.75rem;
  .rc-input-number {
      position: relative;
      flex: 1;
      padding: 0 1px;
      &:hover{
        .rc-input-number-handler-wrap{
            opacity: 1;
        }
      }
  }
  .rc-input-number-input-wrap{
      height: 100%;
      & input {
        width: 100%;
        height: 100%;
        outline: 0;
        border: 0;
        &::placeholder {
          color: ${theme == null ? void 0 : theme.colors.placeholder};
        }
        ${disabled ? "cursor: not-allowed;" : ""}
    }
  }
  .rc-input-number-handler-wrap{
      opacity: 0;
      transition: opacity 0.24s linear 0.1s;
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: center;
      right: 0;
      line-height: 0;
      height: 100%;
      width: 25px;
      ${bordered ? `border-left: 1px solid ${theme == null ? void 0 : theme.colors.border};
             border-right: 1px solid ${theme == null ? void 0 : theme.colors.border};` : ""}
      ${disabled || readOnly ? "display: none;" : ""}
  }
  .rc-input-number-handler-up, 
  .rc-input-number-handler-down{
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover path {
      stroke: ${theme == null ? void 0 : theme.colors.link500};
    }
  }
  .rc-input-number-handler-down {
    ${bordered ? `border-top: 1px solid ${theme == null ? void 0 : theme.colors.border};` : ""}
  }
  .rc-input-number-handler-up-disabled, 
  .rc-input-number-handler-down-disabled {
    cursor: not-allowed;
    background: ${theme == null ? void 0 : theme.colors.background};
    &:hover path {
      stroke: ${theme == null ? void 0 : theme.colors.textSecondary};
    }
  }
`);
const StyledSpace = styled(Shape)`
  display: flex;
  align-items: center;
`;

export { InputNumber };
